import { DeleteFilled, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, App as AntApp, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { deleteCategory, fetchCategoryById } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const CategoryLookupListDetail = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { modal } = AntApp.useApp();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const isLoading =
        lookupSlice.detailsPage.category.updateStatus === StatePieceStatus.IsFetching ||
        lookupSlice.detailsPage.category.fetchStatus === StatePieceStatus.IsFetching;
    const itemId = params.id ? parseInt(params.id) : undefined;

    const removeCategory = () => {
        modal.confirm({
            title: 'Delete category',
            icon: <ExclamationCircleOutlined />,
            content: "Are you sure you want to delete this category? This action can't be undone.",
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                if (itemId) dispatch(deleteCategory(itemId));
            },
        });
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.category.deleteStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/category', { replace: true });
        }
    }, [lookupSlice.detailsPage.category.deleteStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchCategoryById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    if (lookupSlice.detailsPage.category.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <React.Fragment>
            <ContentHeader
                title={`Category #${params.id}`}
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Category',
                        path: '/admin/lookup-lists/category',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.category.data?.code || '-',
                        path: `/admin/lookup-lists/category/${params.id}`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.category.fetchStatus) && (
                    <>
                        <Button type="primary" danger icon={<DeleteFilled />} loading={isLoading} onClick={() => removeCategory()}>
                            Delete option
                        </Button>
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/lookup-lists/category/${params.id}/edit`)}
                        >
                            Edit option details
                        </Button>
                    </>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.category.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.category.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.category.fetchStatus} about={'Category'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Category option Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Id</label>
                                    <p>{lookupSlice.detailsPage.category?.data?.id || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <p>{lookupSlice.detailsPage.category?.data?.code || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <p>{lookupSlice.detailsPage.category?.data?.description || '-'}</p>
                                </Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.category.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.category.data?.created
                                            ? dayjs(lookupSlice.detailsPage.category.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.category.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.category.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.category.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </React.Fragment>
    );
};
